import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import ImageUpload from "@/components/ImageUpload";
import { TEAM } from "@/constants/testIds";
import { ACADEMY_PERMISSIONS } from "@/lib/academyAccess";

const EMPTY_FORM = {
  name: "", email: "", password: "", role: "academy", phone: "", photo_url: null, academy_roles: [],
};

export default function TeamMemberModal({ open, onOpenChange, member, onSubmit }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(member);

  useEffect(() => {
    if (member) {
      setForm({
        name: member.name || "",
        email: member.email || "",
        password: "",
        role: member.role || "academy",
        phone: member.phone || "",
        photo_url: member.photo_url || null,
        academy_roles: member.academy_roles || [],
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [member, open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const toggleAcademy = (key, on) => setForm((f) => ({
    ...f,
    academy_roles: on ? [...f.academy_roles.filter((k) => k !== key), key] : f.academy_roles.filter((k) => k !== key),
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSubmit(form, member);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{isEdit ? "Editar miembro del equipo" : "Nuevo miembro del equipo"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ImageUpload
            currentUrl={form.photo_url}
            onUploaded={(url) => setForm((f) => ({ ...f, photo_url: url }))}
            placeholderUrl={null}
            testId={TEAM.modalPhotoUpload}
          />

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Nombre completo</Label>
            <Input value={form.name} onChange={set("name")} data-testid={TEAM.modalNameInput} placeholder="Nombre y apellido" />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Email</Label>
            <Input type="email" value={form.email} onChange={set("email")} disabled={isEdit} data-testid={TEAM.modalEmailInput} placeholder="correo@ejemplo.com" />
          </div>

          {/* Fix F (2026-10-18): al editar, contraseña nueva para quien se la olvidó */}
          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">{isEdit ? "Contraseña nueva (opcional)" : "Contraseña temporal"}</Label>
            <Input type="password" value={form.password} onChange={set("password")} data-testid={TEAM.modalPasswordInput}
              placeholder={isEdit ? "Vacía = no se cambia" : "Mínimo 6 caracteres"} autoComplete="new-password" />
            {isEdit && <p className="text-xs text-muted-foreground mt-1">Si se olvidó la contraseña, poné una nueva (mínimo 6 caracteres) y pasásela. Después la puede cambiar en Mi Perfil.</p>}
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Teléfono</Label>
            <Input value={form.phone} onChange={set("phone")} data-testid={TEAM.modalPhoneInput} placeholder="Opcional" />
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-1 block">Rol en el sistema</Label>
            <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
              <SelectTrigger data-testid={TEAM.modalRoleSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="academy">Equipo (con los permisos que marques)</SelectItem>
                <SelectItem value="admin">Administración (todos los permisos)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* La administración ya tiene todos los permisos. */}
          {form.role !== "admin" && (
            <div data-testid={TEAM.modalAcademyRoles}>
              <Label className="text-sm text-muted-foreground mb-2 block">Permisos</Label>
              <div className="grid grid-cols-2 gap-2">
                {ACADEMY_PERMISSIONS.map((p) => (
                  <label key={p.key} className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                    <Checkbox
                      checked={form.academy_roles.includes(p.key)}
                      onCheckedChange={(v) => toggleAcademy(p.key, v === true)}
                      data-testid={`${TEAM.modalAcademyRolePrefix}${p.key}`}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
              {form.academy_roles.length === 0 && (
                <p className="text-xs text-destructive mt-2">Marcá al menos un permiso: sin permisos no puede usar la academia.</p>
              )}
            </div>
          )}

        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={TEAM.modalCancelButton}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid={TEAM.modalSaveButton}>
            {saving ? "Guardando..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
